/**
 * osnapSweptRuns — the group-39 THICKNESS sweep, projected onto the SNAP
 * surface: the run the snaps already measure, its image under `t·N̂`, and the
 * RULINGS that join the two.
 *
 * # The defect this closes — @codex PR #785 round 16, the OSNAP fan-out
 *
 * Round 15 (`8c56d08`) made the canvas PAINT the sweep
 * (`adapters/geom/polylineThicknessSweep.ts`), and round 16 gave the pointer
 * pick and the window select the same geometry (`raycast/distance.ts`,
 * `raycast/windowSelect.ts`). The OBJECT SNAPS were left behind, and
 * `polylineThicknessSweep.ts`' own residual 1 says so by name: `osnapCandidates.ts`,
 * `osnapIntersections.ts` and the AI mirror `ai/sceneQuery/osnapQuery.ts` all
 * read `geometry.points` / `polylineCentrelineSamples`, so ENDpoint / MIDpoint /
 * INTERSECTION were offered on the BASE outline only. Measured on the round-16
 * witness — extrusion `(1,1,1)`, thickness 5 on `(0,0) → (10,0)`, sweep vector
 * `t·N̂ = (2.886751345948129, …)`:
 *
 *   width-less   painted top vertex (2.886751345948129, 2.886751345948129)
 *                NEAREST osnap candidate 4.08248290463863 away (the base
 *                endpoint at the origin), against a running-snap aperture of
 *                0.08 world units at an ordinary zoom;
 *   wide band    painted top vertex (3.7032479268758554, 3.7032479268758554),
 *                nearest candidate 5.237183443017883 away;
 *   AI route     the entity was not returned AT ALL for a query at the painted
 *                top vertex (`bboxFromSceneGeometry` maxX 0 vs the painted
 *                2.886751345948129) — a broad-phase under-report, so the exact
 *                resolver never even ran.
 *
 * A user could see an edge, hover it, and find no snap marker. That is a
 * regression introduced by painting the surface, not an inherited limitation:
 * before round 15 there was no visible edge to miss.
 *
 * # WHAT the snaps sweep — the CENTRELINE, and why not the band silhouette
 *
 * The base this module sweeps is whatever run the CALLER already snaps, which
 * for every polyline is its CENTRELINE (the authored vertices, or the
 * tessellated samples of them in the entity's own OCS plane). That is AutoCAD's
 * rule, not an approximation of the drawn shape: a polyline's width is a
 * display property and AutoCAD snaps a wide polyline on its centreline — the
 * same reading `ai/sceneQuery/osnapQuery.ts::polylineFacetToSceneGeometry`
 * already states in its own `widths` comment ("the width BAND is a drawing
 * concern, and AutoCAD snaps a wide polyline on its CENTRELINE"). Group 39, by
 * contrast, IS geometry (Autodesk's DXF reference: the entity is EXTRUDED along
 * its extrusion direction, producing a surface), and AutoCAD snaps the extruded
 * edges of that surface — which is why this module exists and a band-silhouette
 * module does not.
 *
 * So the snap surface is the entity's own geometry UNION its group-39 image,
 * and {@link withPolylineThicknessSweepRuns} — the ONE derivation the batcher,
 * the pick and the window select are all projections of — is what forms it.
 * Nothing here recomputes `p + t·N̂`, the ruling anchoring, or the sweep vector
 * (CLAUDE.md ПРИОРИТЕТ #2.1).
 *
 * # DECLARED RESIDUAL, named rather than left to be rediscovered
 *
 * For a run that ALSO carries width, the canvas paints the sweep of the BAND
 * SILHOUETTE while these snaps offer the sweep of the CENTRELINE, so the top
 * endpoint they offer sits inside the painted top ribbon's outline rather than
 * on it — exactly as the BASE centreline endpoint already sits inside the base
 * band (which `$FILLMODE` 1 at least paints solid). That is the pre-existing
 * AutoCAD convention above extended consistently to the swept half, NOT a new
 * class of answer; closing it would mean deciding that a wide polyline snaps on
 * its silhouette, which would change the UNSWEPT band too and contradict both
 * AutoCAD and the route comment quoted above. Pinned as an explicit property
 * (`__tests__/thicknessSweptSnap.property.test.ts`, P-S4) so it is a measured
 * boundary rather than an accident.
 */

import {
  withPolylineThicknessSweepRuns,
  type ThicknessSweepGeometry,
} from "../adapters/geom/polylineThicknessSweep";
import type { Vec3 } from "../types";

/** The `polyline` geometry fields the snap sweep reads. */
export interface OsnapSweptGeometry extends ThicknessSweepGeometry {
  /** The AUTHORED vertices — the anchors the drawn rulings stand on. */
  readonly points: readonly Vec3[];
  readonly closed: boolean;
}

/** One run of the snap surface. */
export interface OsnapSweptRun {
  readonly points: readonly Vec3[];
  readonly closed: boolean;
  /**
   * `true` for a RULING — the straight 2-point edge joining a base vertex to
   * its image. A ruling is straight WHATEVER the base declares (a group-42
   * bulge bows a span, never the extrusion direction), so a caller must not
   * carry the authored bulge list onto it.
   */
  readonly ruling: boolean;
}

/**
 * The snap surface of `geometry`, given the run `base` the caller already
 * snaps: `[base]` when the entity declares no group-39 thickness, otherwise
 * `[base, base + t·N̂, …one ruling per authored vertex]`.
 *
 * The order is {@link withPolylineThicknessSweepRuns}' own — base, the
 * translated copy, then the rulings — which `raycast/thicknessSweptPick.property.test.ts`
 * already pins segment-for-segment against the batcher's paint stream. The
 * RULING ANCHORS are `geometry.points`, the AUTHORED vertices, which is what
 * `drawnPolylineSweptRuns` anchors a width-less run's rulings at and what
 * AutoCAD's wireframe of an extruded polyline shows: a ruling at each corner,
 * never one per tessellation sample of a bowed span.
 *
 * A base of FEWER THAN TWO points answers `[base]` even when a thickness is
 * declared — the same gate `drawnPolylineSweptRuns` applies, and for the
 * same reason: the canvas paints no edge for a single-vertex polyline, so
 * handing back a ruling would make snappable exceed drawn, the direction this
 * whole round is about.
 */
export function osnapPolylineSweptRuns(
  geometry: OsnapSweptGeometry,
  base: readonly Vec3[],
): readonly OsnapSweptRun[] {
  const baseRun: OsnapSweptRun = {
    points: base,
    closed: geometry.closed,
    ruling: false,
  };
  if (!geometry.thicknessSweep || base.length < 2) return [baseRun];

  const swept = withPolylineThicknessSweepRuns(
    geometry,
    [{ points: base, closed: geometry.closed }],
    geometry.points,
  );
  // ONE base run in, so run 0 is that base and run 1 is its translated copy;
  // everything after is a ruling. The split is the documented order of the
  // shared derivation, not a re-derivation of which run is which.
  const outlines = 2;
  return swept.map((run, i) => ({
    points: run.points,
    closed: run.closed,
    ruling: i >= outlines,
  }));
}
